import React, { useState, useEffect, useMemo } from 'react';
import { Layers, RefreshCw } from 'lucide-react';

export default function IkasCustomerCategorySegments() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setRows([]); return; }
      // Demo fallback: synthesize category purchase segments for demo brand
      const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (brandId === DEMO_BRAND_ID) {
        const mkRowsForCat = (category, tiers) => {
          const out = [];
          const month = new Date().toISOString().slice(0, 10);
          let seq = 1;
          for (let i = 0; i < tiers.ge4; i++) {
            out.push({ month, customer_id: `${category}-U${String(seq++).padStart(4, '0')}`, category_name: category, orders_count: 4 });
          }
          for (let i = 0; i < tiers.ge3Only; i++) {
            out.push({ month, customer_id: `${category}-U${String(seq++).padStart(4, '0')}`, category_name: category, orders_count: 3 });
          }
          for (let i = 0; i < tiers.ge2Only; i++) {
            out.push({ month, customer_id: `${category}-U${String(seq++).padStart(4, '0')}`, category_name: category, orders_count: 2 });
          }
          for (let i = 0; i < tiers.ge1Only; i++) {
            out.push({ month, customer_id: `${category}-U${String(seq++).padStart(4, '0')}`, category_name: category, orders_count: 1 });
          }
          return out;
        };
        const demoRows = [
          ...mkRowsForCat('Shoes', { ge4: 18, ge3Only: 20, ge2Only: 36, ge1Only: 36 }),       // 110, 74, 38, 18
          ...mkRowsForCat('Clothing', { ge4: 22, ge3Only: 18, ge2Only: 40, ge1Only: 40 }),    // 120, 80, 40, 22
          ...mkRowsForCat('Accessories', { ge4: 7, ge3Only: 10, ge2Only: 20, ge1Only: 30 })   // 67, 37, 17, 7
        ];
        setRows(demoRows);
        return;
      }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/ikas/customers/category-monthly`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const data = res.ok && js?.rows ? js.rows : [];
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categorySegments = useMemo(() => {
    // rows: [{ month, customer_id, category_name, orders_count }]
    const byCategory = new Map();
    for (const r of rows || []) {
      const category = r.category_name || 'Unknown';
      const cid = r.customer_id;
      const orders = Number(r.orders_count || 0);
      if (!cid) continue;
      if (!byCategory.has(category)) byCategory.set(category, new Map());
      const perCust = byCategory.get(category);
      perCust.set(cid, (perCust.get(cid) || 0) + orders);
    }
    // Compute thresholds per category
    const result = [];
    for (const [category, perCust] of byCategory.entries()) {
      let ge1 = 0, ge2 = 0, ge3 = 0, ge4 = 0;
      for (const total of perCust.values()) {
        if (total >= 1) ge1++;
        if (total >= 2) ge2++;
        if (total >= 3) ge3++;
        if (total >= 4) ge4++;
      }
      result.push({
        category,
        counts: { ge1, ge2, ge3, ge4 }
      });
    }
    // Sort descending by ge1
    result.sort((a, b) => (b.counts.ge1 - a.counts.ge1) || a.category.localeCompare(b.category));
    return result;
  }, [rows]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/ikaslogoicon.png"
              alt="Ikas"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Customer Category Segments</h1>
              <p className="text-sm text-gray-500">Per category segments: ≥1, ≥2, ≥3, ≥4 orders.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={refresh}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Category list with segments */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        {categorySegments.length === 0 ? (
          <div className="text-sm text-gray-500">No data.</div>
        ) : (
          <div className="space-y-4">
            {categorySegments.map(({ category, counts }) => (
              <div key={category} className="border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    <Layers size={16} className="text-indigo-600" />
                    <span>{category}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 1 order</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge1.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 2 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge2.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 3 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge3.toLocaleString()}</div>
                  </div>
                  <div className="border rounded-lg p-3 bg-indigo-50/40">
                    <div className="text-xs text-gray-600">At least 4 orders</div>
                    <div className="text-xl font-bold text-gray-900 mt-1">{counts.ge4.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


